import * as THREE from 'three';
import { LIGHT_PRISM } from './constants';
import { LASLoader } from './LASLoader';

// Set to true to show debug information
const DEBUG = false;

// Helper function to shuffle an array
function shuffleArray(array) {
  for (let i = array.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [array[i], array[j]] = [array[j], array[i]];
  }
}

// Helper to apply jitter to loading order for more natural appearance
function applyLoadingJitter(points, jitterAmount) {
  const chunkSize = Math.floor(points.length * jitterAmount);
  if (chunkSize <= 1) return;
  
  for (let i = 0; i < points.length; i += chunkSize) {
    const end = Math.min(i + chunkSize, points.length);
    const chunk = points.slice(i, end);
    shuffleArray(chunk);
    
    for (let j = 0; j < chunk.length; j++) {
      points[i + j] = chunk[j];
    }
  }
}

// Helper function to get color from gradient based on height
function getColorFromGradient(normalizedHeight, gradient) {
  // Handle edge cases
  if (normalizedHeight <= gradient[0].height) {
    return gradient[0].color;
  }
  if (normalizedHeight >= gradient[gradient.length - 1].height) {
    return gradient[gradient.length - 1].color;
  }
  
  // Find and interpolate between gradient stops
  for (let i = 0; i < gradient.length - 1; i++) {
    if (normalizedHeight >= gradient[i].height && 
        normalizedHeight < gradient[i + 1].height) {
      
      // Linear interpolation between colors for smoother gradients
      const t = (normalizedHeight - gradient[i].height) / 
               (gradient[i + 1].height - gradient[i].height);
      
      const color1 = new THREE.Color(gradient[i].color);
      const color2 = new THREE.Color(gradient[i + 1].color);
      
      return color1.lerp(color2, t).getHex();
    }
  }
  
  // Fallback
  return 0xFFFFFF;
}

// Helper function to get color from intensity
function getColorFromIntensity(normalizedIntensity) {
  // Color ramp from blue (low) through cyan, green, yellow to red (high)
  if (normalizedIntensity < 0.25) {
    // Blue to cyan
    return new THREE.Color(0, normalizedIntensity * 4, 1).getHex();
  } else if (normalizedIntensity < 0.5) {
    // Cyan to green
    return new THREE.Color(0, 1, 1 - (normalizedIntensity - 0.25) * 4).getHex();
  } else if (normalizedIntensity < 0.75) {
    // Green to yellow
    return new THREE.Color((normalizedIntensity - 0.5) * 4, 1, 0).getHex();
  } else {
    // Yellow to red
    return new THREE.Color(1, 1 - (normalizedIntensity - 0.75) * 4, 0).getHex();
  }
}

// Helper function to get color from classification
function getColorFromClassification(classification) {
  // Standard LAS classification colors
  switch(classification) {
    case 0: // Created, never classified
    case 1: // Unclassified
      return 0xCCCCCC; // Light gray
    case 2: // Ground
      return 0x8B4513; // Brown
    case 3: // Low vegetation
      return 0x7CFC00; // Lawn green
    case 4: // Medium vegetation
      return 0x32CD32; // Lime green
    case 5: // High vegetation
      return 0x006400; // Dark green
    case 6: // Building
      return 0xFF0000; // Red
    case 7: // Low point (noise)
      return 0x000000; // Black
    case 9: // Water
      return 0x0000FF; // Blue
    case 11: // Road surface
      return 0xFFFF00; // Yellow
    case 17: // Bridge deck
      return 0xA52A2A; // Brown
    default:
      return 0xAAAAAA; // Gray for other classes
  }
}

/**
 * Creates a point cloud from a LAS file that gradually loads points into the scene
 */
const createLidarPointCloud = () => {
  // Create a main group for the point cloud
  const pointCloudGroup = new THREE.Group();
  
  // Constants for point cloud from configuration
  const {
    POSITION,
    SIZE,
    WIREFRAME_COLOR,
    POINT_CLOUD,
    LOADING
  } = LIGHT_PRISM.TERRAIN;
  
  // Position the point cloud group
  pointCloudGroup.position.set(
    POSITION.X - 1.5, // Move slightly to the left
    POSITION.Y - 3,   // Move further down for better visibility
    POSITION.Z
  );
  
  // Rotate point cloud 90 degrees around X axis
  pointCloudGroup.rotation.x = Math.PI / 2;
  pointCloudGroup.rotation.y = Math.PI;
  pointCloudGroup.rotation.z = Math.PI / 2;
  
  // Create placeholder geometries while loading
  const planeGeometry = new THREE.PlaneGeometry(SIZE, SIZE, 1, 1);
  planeGeometry.rotateX(-Math.PI / 2); // Horizontal plane
  planeGeometry.rotateY(Math.PI); // Match the 180-degree Y rotation
  planeGeometry.rotateZ(Math.PI / 2); // Match the 90-degree Z rotation
  
  // Create material for the placeholder
  const wireMaterial = new THREE.MeshBasicMaterial({
    color: WIREFRAME_COLOR,
    wireframe: true,
    transparent: true,
    opacity: 0.5
  });
  
  // Create a temporary wireframe mesh as placeholder
  const wireMesh = new THREE.Mesh(planeGeometry, wireMaterial);
  pointCloudGroup.add(wireMesh);
  
  // Loading state tracking
  const loadingState = {
    loaded: false,
    loadStartTime: 0,
    totalPoints: 0,
    loadedPoints: 0,
    pointsToAdd: [],
    pointCloud: null,
    pointMaterial: null,
    currentChunk: 0,
    totalChunks: 0,
    header: null,
    useChunks: POINT_CLOUD.CHUNK_SIZE > 0, // Enable chunk loading if chunk size is positive
    // Arrays to manage the loading rays
    rays: [],
    rayTimes: [],
    rayMaterial: null,
    rayGroup: new THREE.Group(), // Group to hold the loading rays
    // Track point cloud for reference
    pointCloudGroup: pointCloudGroup
  };
  
  // Do NOT add rayGroup to the pointCloudGroup - it will be added to the scene later
  // This prevents the rays from rotating with the point cloud
  
  // Create point material with better settings for visibility
  const pointMaterial = new THREE.PointsMaterial({
    size: POINT_CLOUD.POINT_SIZE,
    vertexColors: true,
    sizeAttenuation: true,
    transparent: false,
    alphaTest: 0.1  // Help with rendering order
  });
  loadingState.pointMaterial = pointMaterial;
  
  // Create material for the loading rays
  const rayMaterial = new THREE.LineBasicMaterial({
    color: LOADING.RAY_COLOR,
    transparent: true,
    opacity: 0.7,
    linewidth: LOADING.RAY_WIDTH
  });
  loadingState.rayMaterial = rayMaterial;
  
  // Select a random LAS file from the available options
  // Get the list of LAS files from constants
  const lasFiles = POINT_CLOUD.LAS_FILES || ['/lidar/dp2.las']; // Fallback if not defined
  
  // Randomly select one file from the array
  const randomIndex = Math.floor(Math.random() * lasFiles.length);
  const lasPath = lasFiles[randomIndex];
  
  // Store the selected file path in loadingState for later reference
  loadingState.selectedLasFile = lasPath;
  
  console.log(`Loading LAS file (${randomIndex + 1}/${lasFiles.length}): ${lasPath}`);
  
  // Create a function to generate rays and add it to the loadingState
  loadingState.createRay = (targetPos) => {
    // Create line geometry
    const geometry = new THREE.BufferGeometry();
    
    // Get the second prism details from constants
    const { SECOND_PRISM } = LIGHT_PRISM;
    
    // Fixed prism position in world space - use middle of the prism
    const prismWorldPos = {
      x: SECOND_PRISM.POSITION.X,  // X: 5 (Earth position + 12)
      y: SECOND_PRISM.POSITION.Y - 0.5,  // Y: 7 (Earth position + RADIUS + 2) - 0.5 for middle
      z: SECOND_PRISM.POSITION.Z + 0.5    // Z: 0
    };
    
    // The point cloud target position needs to be transformed to world space
    // We need to apply the point cloud's position and rotation to the target position
    
    // Create a temporary vector to hold the target position
    const targetVector = new THREE.Vector3(targetPos.x, targetPos.y, targetPos.z);
    
    // Get a reference to the point cloud group
    const pointCloudGroup = loadingState.pointCloudGroup;
    
    // Apply the point cloud's matrix to transform the target position to world space
    // This accounts for the point cloud's position, rotation, and scale
    targetVector.applyMatrix4(pointCloudGroup.matrix);
    
    // Store ray data for later updates
    const rayData = {
      prismPos: prismWorldPos,                  // Fixed prism position (world space)
      localTargetPos: { ...targetPos },         // Original target in point cloud local space
      worldTargetPos: {                         // Target in world space (updated when cloud rotates)
        x: targetVector.x,
        y: targetVector.y,
        z: targetVector.z
      },
      lastUpdate: Date.now() / 1000            // Time tracking for ray updates
    };
    
    // Set positions from source to target (both in world space)
    const positions = new Float32Array([
      prismWorldPos.x, prismWorldPos.y, prismWorldPos.z,   // Source: fixed prism position (world space)
      targetVector.x, targetVector.y, targetVector.z       // Target: current point position (world space)
    ]);

    geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));

    // Create the line
    const ray = new THREE.Line(geometry, loadingState.rayMaterial);
    
    // Store the ray data with the ray object for updates
    ray.userData = rayData;

    // Add to the ray group (which is now directly in the scene, not in the point cloud)
    loadingState.rayGroup.add(ray);

    // Store the ray reference and creation time
    loadingState.rays.push(ray);
    loadingState.rayTimes.push(Date.now() / 1000);

    return ray;
  };
  
  // Function to handle a LAS chunk
  const handleLASChunk = (result) => {
    // If this is first chunk, create geometry and setup
    if (loadingState.currentChunk === 0) {
      // Remove placeholder on first chunk
      pointCloudGroup.remove(wireMesh);
      wireMesh.geometry.dispose();
      wireMesh.material.dispose();
      
      // Store total points and header info
      loadingState.totalPoints = result.totalChunks * LOADING.CHUNK_SIZE;
      loadingState.header = result.header;
      loadingState.totalChunks = result.totalChunks;
      
      console.log(`Starting chunk loading: ${result.totalChunks} chunks of ${LOADING.CHUNK_SIZE} points each`);
      
      // Create buffer geometry for point cloud
      // Reserve space for all points upfront
      const maxPoints = Math.min(POINT_CLOUD.POINT_BUDGET || 500000, result.header.numberOfPointRecords);
      const positions = new Float32Array(maxPoints * 3);
      const colors = new Float32Array(maxPoints * 3);
      
      const pointGeometry = new THREE.BufferGeometry();
      pointGeometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
      pointGeometry.setAttribute('color', new THREE.BufferAttribute(colors, 3));
      
      // Create point cloud object
      const pointCloud = new THREE.Points(pointGeometry, pointMaterial);
      pointCloudGroup.add(pointCloud);
      loadingState.pointCloud = pointCloud;
      
      // Initialize bounds
      loadingState.bounds = {
        min: { x: Infinity, y: Infinity, z: Infinity },
        max: { x: -Infinity, y: -Infinity, z: -Infinity }
      };
      
      // Initialize intensity range
      loadingState.intensity = {
        min: Infinity,
        max: -Infinity
      };
    }
    
    // Process points from this chunk
    if (result.points && result.points.length > 0) {
      const points = result.points;
      console.log(`Processing chunk ${result.chunkIndex}/${result.totalChunks} with ${points.length} points`);
      
      // Update bounds
      for (const point of points) {
        loadingState.bounds.min.x = Math.min(loadingState.bounds.min.x, point.x);
        loadingState.bounds.min.y = Math.min(loadingState.bounds.min.y, point.y);
        loadingState.bounds.min.z = Math.min(loadingState.bounds.min.z, point.z);
        
        loadingState.bounds.max.x = Math.max(loadingState.bounds.max.x, point.x);
        loadingState.bounds.max.y = Math.max(loadingState.bounds.max.y, point.y);
        loadingState.bounds.max.z = Math.max(loadingState.bounds.max.z, point.z);
        
        // Update intensity range if available
        if (point.intensity !== undefined) {
          loadingState.intensity.min = Math.min(loadingState.intensity.min, point.intensity);
          loadingState.intensity.max = Math.max(loadingState.intensity.max, point.intensity);
        }
      }
      
      // Calculate center and scale if this is final chunk
      if (result.isLastChunk || result.chunkIndex === result.totalChunks - 1) {
        const { min, max } = loadingState.bounds;
        
        // Calculate center and size for scaling
        const centerX = (min.x + max.x) / 2;
        const centerY = (min.y + max.y) / 2;
        const centerZ = (min.z + max.z) / 2;
        
        const sizeX = max.x - min.x;
        const sizeY = max.y - min.y;
        const sizeZ = max.z - min.z;
        
        // Scale to fit within terrain size
        const maxDim = Math.max(sizeX, sizeY, sizeZ);
        const scale = SIZE / maxDim * 0.9; // 90% of available space
        
        console.log(`Final scaling: center(${centerX.toFixed(2)}, ${centerY.toFixed(2)}, ${centerZ.toFixed(2)}), scale=${scale.toFixed(4)}`);
        
        // Store scaling factors
        loadingState.transform = {
          centerX, centerY, centerZ,
          scale
        };
      }
      
      // Get the current transform
      const transform = loadingState.transform || {
        centerX: 0, centerY: 0, centerZ: 0,
        scale: 1
      };
      
      const { centerX, centerY, centerZ, scale } = transform;
      
      // Get color mode from settings
      const colorMode = POINT_CLOUD.COLOR_ENCODING || 'ELEVATION';
      const isRgbMode = colorMode === 'RGB';
      
      // For intensity and classification modes, normalize if we have the data
      const minIntensity = isFinite(loadingState.intensity.min) ? loadingState.intensity.min : 0;
      const maxIntensity = isFinite(loadingState.intensity.max) ? loadingState.intensity.max : 255;
      const minY = loadingState.bounds.min.y;
      const maxY = loadingState.bounds.max.y;
      
      // Process points for this chunk
      const pointsToAdd = [];
      
      for (let i = 0; i < points.length; i++) {
        const point = points[i];
        
        // Scale and center coordinates
        const x = (point.x - centerX) * scale;
        // Flip Y to match our coordinate system
        const y = (point.y - centerY) * scale;
        const z = (point.z - centerZ) * scale;
        
        // Determine color based on selected mode
        let color;
        
        if (colorMode === 'RGB' && point.color) {
          // Use color directly from LAS file
          // The values are already normalized to 0-1 range in the LASLoader
          const r = point.color.r;
          const g = point.color.g;
          const b = point.color.b;
          
          // Create THREE.js color with RGB values
          color = new THREE.Color(r, g, b);
          
          // The Dana Point LAS file has special color handling already in LASLoader
          // Only apply additional enhancements if ENHANCE_COLORS is enabled
          const selectedFile = loadingState.selectedLasFile;
          const isDanaPoint = selectedFile.includes("dp2.las") || selectedFile.includes("DP-strip.las") || 
                              selectedFile.includes("DP.las") || selectedFile.includes("dana-point.las");
          
          if (POINT_CLOUD.ENHANCE_COLORS && !isDanaPoint) {
            // Get color adjustment settings
            const {
              GAMMA = 0.8,
              SATURATION = 1.3,
              MIN_LIGHTNESS = 0.2,
              MAX_LIGHTNESS = 0.9
            } = POINT_CLOUD.COLOR_ADJUSTMENT || {};
            
            // Apply color enhancements
            color.r = Math.pow(color.r, GAMMA);
            color.g = Math.pow(color.g, GAMMA); 
            color.b = Math.pow(color.b, GAMMA);
            
            // Convert to HSL for better control over saturation and lightness
            const hsl = {h: 0, s: 0, l: 0};
            THREE.ColorManagement.enabled = true;
            color.getHSL(hsl);
            
            // Enhance saturation slightly (using configurable multiplier)
            hsl.s = Math.min(1.0, hsl.s * SATURATION);
            
            // Ensure good lightness levels (not too dark, not too bright)
            if (hsl.l < MIN_LIGHTNESS) hsl.l = MIN_LIGHTNESS; // Boost dark colors
            if (hsl.l > MAX_LIGHTNESS) hsl.l = MAX_LIGHTNESS; // Reduce bright colors
            
            // Convert back to RGB
            color.setHSL(hsl.h, hsl.s, hsl.l);
          } 
          // Dana Point files already have color enhancement in LASLoader.js
          else if (isDanaPoint) {
            // Just ensure good brightness and saturation levels 
            const hsl = {h: 0, s: 0, l: 0};
            color.getHSL(hsl);
            
            // Very gentle brightness and saturation adjustment 
            if (hsl.l < 0.2) hsl.l = 0.2;    // Boost dark colors slightly
            if (hsl.l > 0.9) hsl.l = 0.9;    // Reduce very bright colors
            if (hsl.s < 0.3) hsl.s = 0.3;    // Ensure minimum saturation
            
            // Convert back to RGB
            color.setHSL(hsl.h, hsl.s, hsl.l);
          }
        } else if (colorMode === 'RGB' && !point.color) {
          // If RGB mode is selected but no color data is available,
          // fall back to intensity or elevation
          if (point.intensity !== undefined) {
            const normalizedIntensity = (point.intensity - minIntensity) / (maxIntensity - minIntensity);
            color = new THREE.Color(getColorFromIntensity(normalizedIntensity));
          } else {
            // Use elevation as last resort
            const normalizedHeight = (point.y - minY) / (maxY - minY || 1);
            color = new THREE.Color(getColorFromGradient(normalizedHeight, POINT_CLOUD.ELEVATION_GRADIENT));
          }
        } else if (colorMode === 'INTENSITY' && point.intensity !== undefined) {
          // Intensity-based coloring
          const normalizedIntensity = (point.intensity - minIntensity) / (maxIntensity - minIntensity);
          color = new THREE.Color(getColorFromIntensity(normalizedIntensity));
        } else if (colorMode === 'CLASSIFICATION' && point.classification !== undefined) {
          // Classification-based coloring
          color = new THREE.Color(getColorFromClassification(point.classification));
        } else {
          // Default to elevation-based coloring
          const normalizedHeight = (point.y - minY) / (maxY - minY || 1);
          color = new THREE.Color(getColorFromGradient(normalizedHeight, POINT_CLOUD.ELEVATION_GRADIENT));
        }
        
        // Add to processing queue
        pointsToAdd.push({
          // Calculate the global index across all chunks
          index: point.index,
          position: { x, y, z },
          color,
          distFromCenter: Math.sqrt(x * x + z * z) // For wave pattern loading
        });
      }
      
      // Order points based on loading settings
      if (LOADING.RANDOM_ORDER) {
        // Random order
        shuffleArray(pointsToAdd);
      } else if (LOADING.USE_WAVE_PATTERN) {
        // Wave pattern (center first)
        pointsToAdd.sort((a, b) => a.distFromCenter - b.distFromCenter);
        
        // Add jitter for more natural appearance
        if (LOADING.JITTER_AMOUNT > 0) {
          applyLoadingJitter(pointsToAdd, LOADING.JITTER_AMOUNT);
        }
      }
      
      // Add points to the main queue
      loadingState.pointsToAdd = loadingState.pointsToAdd.concat(pointsToAdd);
      
      // Set loading to begin or continue
      if (!loadingState.loaded) {
        loadingState.loaded = true;
        loadingState.loadStartTime = Date.now() / 1000 + LOADING.INITIAL_DELAY;
      }
      
      // Update current chunk
      loadingState.currentChunk = result.chunkIndex + 1;
      
      // If not the last chunk, load next chunk after delay
      if (!result.isLastChunk && result.chunkIndex < result.totalChunks - 1) {
        setTimeout(() => {
          // Load the next chunk
          loadNextChunk();
        }, LOADING.CHUNK_DELAY * 1000);
      }
    }
  };
  
  // Function to load the next chunk
  const loadNextChunk = () => {
    const lasLoader = new LASLoader();
    
    console.log(`Loading chunk ${loadingState.currentChunk} of ${loadingState.totalChunks}`);
    
    // Load the next chunk
    lasLoader.load(lasPath, handleLASChunk, null, null, {
      useChunks: true,
      chunkSize: LOADING.CHUNK_SIZE,
      chunkIndex: loadingState.currentChunk
    });
  };
  
  // Create and use the LAS loader
  const lasLoader = new LASLoader();
  
  // Load the first chunk or entire file
  if (loadingState.useChunks) {
    console.log(`Using chunk loading with chunk size: ${LOADING.CHUNK_SIZE}`);
    
    // Load the first chunk
    lasLoader.load(lasPath, handleLASChunk, null, null, {
      useChunks: true,
      chunkSize: LOADING.CHUNK_SIZE,
      chunkIndex: 0
    });
  } else {
    // Load the entire file at once (old method)
    lasLoader.load(lasPath, (points) => {
      if (!points || points.length === 0) {
        console.error('No points loaded from LAS file');
        return;
      }
      
      console.log(`Successfully loaded ${points.length} points from LAS file`);
      
      // Remove placeholder
      pointCloudGroup.remove(wireMesh);
      wireMesh.geometry.dispose();
      wireMesh.material.dispose();
    
    // Limit points to a reasonable number for performance
    const MAX_POINTS = POINT_CLOUD.POINT_BUDGET || 500000;
    let pointsToProcess = points;
    
    if (points.length > MAX_POINTS) {
      console.log(`Sampling points: ${points.length} → ${MAX_POINTS}`);
      
      // Subsample points evenly
      const stride = Math.ceil(points.length / MAX_POINTS);
      pointsToProcess = [];
      
      for (let i = 0; i < points.length; i += stride) {
        pointsToProcess.push(points[i]);
      }
    }
    
    // Store point count
    loadingState.totalPoints = pointsToProcess.length;
    
    // Create buffer geometry for point cloud
    const positions = new Float32Array(pointsToProcess.length * 3);
    const colors = new Float32Array(pointsToProcess.length * 3);
    
    const pointGeometry = new THREE.BufferGeometry();
    pointGeometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
    pointGeometry.setAttribute('color', new THREE.BufferAttribute(colors, 3));
    
    // Create point cloud object
    const pointCloud = new THREE.Points(pointGeometry, pointMaterial);
    pointCloudGroup.add(pointCloud);
    loadingState.pointCloud = pointCloud;
    
    // Find point cloud bounds
    let minX = Infinity, minY = Infinity, minZ = Infinity;
    let maxX = -Infinity, maxY = -Infinity, maxZ = -Infinity;
    
    // Calculate bounds
    for (const point of pointsToProcess) {
      minX = Math.min(minX, point.x);
      minY = Math.min(minY, point.y);
      minZ = Math.min(minZ, point.z);
      maxX = Math.max(maxX, point.x);
      maxY = Math.max(maxY, point.y);
      maxZ = Math.max(maxZ, point.z);
    }
    
    if (DEBUG) {
      console.log('Point cloud bounds:', {
        min: [minX, minY, minZ],
        max: [maxX, maxY, maxZ],
        size: [maxX - minX, maxY - minY, maxZ - minZ]
      });
    }
    
    // Calculate center and size for scaling
    const centerX = (minX + maxX) / 2;
    const centerY = (minY + maxY) / 2;
    const centerZ = (minZ + maxZ) / 2;
    
    const sizeX = maxX - minX;
    const sizeY = maxY - minY;
    const sizeZ = maxZ - minZ;
    
    // Scale to fit within terrain size
    const maxDim = Math.max(sizeX, sizeY, sizeZ);
    const scale = SIZE / maxDim * 0.9; // 90% of available space
    
    console.log(`Scaling point cloud by ${scale.toFixed(4)}`);
    
    // Calculate intensity range for normalization
    let minIntensity = Infinity;
    let maxIntensity = -Infinity;
    
    for (const point of pointsToProcess) {
      if (point.intensity !== undefined) {
        minIntensity = Math.min(minIntensity, point.intensity);
        maxIntensity = Math.max(maxIntensity, point.intensity);
      }
    }
    
    // Default intensity range if invalid
    if (!isFinite(minIntensity) || !isFinite(maxIntensity) || minIntensity === maxIntensity) {
      minIntensity = 0;
      maxIntensity = 255;
    }
    
    // Get color mode from settings
    const colorMode = POINT_CLOUD.COLOR_ENCODING || 'ELEVATION';
    console.log(`Using color mode: ${colorMode}`);
    
    // Special color extraction for Dana Point file if RGB mode is enabled
    const isRgbMode = colorMode === 'RGB';
    
    // Process and prepare points for loading
    const pointsToAdd = [];
    
    for (let i = 0; i < pointsToProcess.length; i++) {
      const point = pointsToProcess[i];
      
      // Scale and center coordinates
      const x = (point.x - centerX) * scale;
      // Flip Y to match our coordinate system
      const y = (point.y - centerY) * scale;
      const z = (point.z - centerZ) * scale;
      
      // Determine color based on selected mode
      let color;
      
      if (colorMode === 'RGB' && point.color) {
        // Use color directly from LAS file
        // The values are already normalized to 0-1 range in the LASLoader
        const r = point.color.r;
        const g = point.color.g;
        const b = point.color.b;
        
        // Create THREE.js color with RGB values
        color = new THREE.Color(r, g, b);
        
        // The Dana Point LAS file has special color handling already in LASLoader
        // Only apply additional enhancements if ENHANCE_COLORS is enabled and
        // we're not working with Dana Point (to avoid double-enhancement)
        const selectedFile = loadingState.selectedLasFile;
        const isDanaPoint = selectedFile.includes("dp2.las") || selectedFile.includes("DP-strip.las") || 
                            selectedFile.includes("DP.las") || selectedFile.includes("dana-point.las");
        
        if (POINT_CLOUD.ENHANCE_COLORS && !isDanaPoint) {
          // Get color adjustment settings
          const {
            GAMMA = 0.8,
            SATURATION = 1.3,
            MIN_LIGHTNESS = 0.2,
            MAX_LIGHTNESS = 0.9
          } = POINT_CLOUD.COLOR_ADJUSTMENT || {};
          
          // More sophisticated color enhancement
          // 1. Apply gamma correction for better visibility
          // 2. Boost saturation slightly to make colors more vivid
          // 3. Ensure good brightness levels
          
          // First, apply gamma correction for more natural appearance
          // color.r = Math.pow(color.r, GAMMA);
          // color.g = Math.pow(color.g, GAMMA);
          // color.b = Math.pow(color.b, GAMMA);
          
          // Convert to HSL for better control over saturation and lightness
          const hsl = {h: 0, s: 0, l: 0};
          THREE.ColorManagement.enabled = true;
          color.getHSL(hsl);
          
          // Enhance saturation slightly (using configurable multiplier)
          hsl.s = Math.min(1.0, hsl.s * SATURATION);
          
          // Ensure good lightness levels (not too dark, not too bright)
          if (hsl.l < MIN_LIGHTNESS) hsl.l = MIN_LIGHTNESS; // Boost dark colors
          if (hsl.l > MAX_LIGHTNESS) hsl.l = MAX_LIGHTNESS; // Reduce bright colors
          
          // Convert back to RGB
          color.setHSL(hsl.h, hsl.s, hsl.l);
        } 
        // Dana Point files already have color enhancement in LASLoader.js
        // We'll only make minimal adjustments here if needed
        else if (isDanaPoint) {
          // Dana Point colors are already enhanced in LASLoader.js
          // Just ensure good brightness and saturation levels 
          const hsl = {h: 0, s: 0, l: 0};
          color.getHSL(hsl);
          
          // Very gentle brightness and saturation adjustment 
          if (hsl.l < 0.2) hsl.l = 0.2;    // Boost dark colors slightly
          if (hsl.l > 0.9) hsl.l = 0.9;    // Reduce very bright colors
          if (hsl.s < 0.3) hsl.s = 0.3;    // Ensure minimum saturation
          
          // Convert back to RGB
          color.setHSL(hsl.h, hsl.s, hsl.l);
          
          // Log first few point colors for debugging
          if (i < 3) {
            console.log(`Dana Point final color: R=${color.r.toFixed(4)}, G=${color.g.toFixed(4)}, B=${color.b.toFixed(4)}`);
          }
        }
        
        // Log first few colors for debugging
        if (i < 5) {
          console.log(`Point ${i} color from LAS: R=${r.toFixed(4)}, G=${g.toFixed(4)}, B=${b.toFixed(4)}`);
        }
      } else if (colorMode === 'RGB' && !point.color) {
        // If RGB mode is selected but no color data is available,
        // fall back to a default color based on intensity or elevation
        if (point.intensity !== undefined) {
          const normalizedIntensity = (point.intensity - minIntensity) / (maxIntensity - minIntensity);
          color = new THREE.Color(getColorFromIntensity(normalizedIntensity));
          
          if (i < 5) {
            console.log(`No RGB color available, using intensity fallback`);
          }
        } else {
          // Use elevation as last resort
          const normalizedHeight = (point.y - minY) / (maxY - minY || 1);
          color = new THREE.Color(getColorFromGradient(normalizedHeight, POINT_CLOUD.ELEVATION_GRADIENT));
          
          if (i === 0) {
            console.log(`No RGB color or intensity available, using elevation fallback`);
          }
        }
      } else if (colorMode === 'INTENSITY' && point.intensity !== undefined) {
        // Intensity-based coloring
        const normalizedIntensity = (point.intensity - minIntensity) / (maxIntensity - minIntensity);
        color = new THREE.Color(getColorFromIntensity(normalizedIntensity));
      } else if (colorMode === 'CLASSIFICATION' && point.classification !== undefined) {
        // Classification-based coloring
        color = new THREE.Color(getColorFromClassification(point.classification));
      } else {
        // Default to elevation-based coloring
        const normalizedHeight = (point.y - minY) / (maxY - minY || 1);
        color = new THREE.Color(getColorFromGradient(normalizedHeight, POINT_CLOUD.ELEVATION_GRADIENT));
      }
      
      // Add to processing queue
      pointsToAdd.push({
        index: i,
        position: { x, y, z },
        color,
        distFromCenter: Math.sqrt(x * x + z * z) // For wave pattern loading
      });
    }
    
    // Order points based on loading settings
    if (LOADING.RANDOM_ORDER) {
      // Random order
      shuffleArray(pointsToAdd);
    } else if (LOADING.USE_WAVE_PATTERN) {
      // Wave pattern (center first)
      pointsToAdd.sort((a, b) => a.distFromCenter - b.distFromCenter);
      
      // Add jitter for more natural appearance
      if (LOADING.JITTER_AMOUNT > 0) {
        applyLoadingJitter(pointsToAdd, LOADING.JITTER_AMOUNT);
      }
    }
    
    // Store processed points
    loadingState.pointsToAdd = pointsToAdd;
    
    // Set loading to begin after delay
    loadingState.loaded = true;
    loadingState.loadStartTime = Date.now() / 1000 + LOADING.INITIAL_DELAY;
    });
  }
  
  return {
    pointCloudGroup,
    loadingState
  };
};

/**
 * Updates the point cloud loading and animation
 */
const updateLidarPointCloud = (pointCloud) => {
  if (!pointCloud || !pointCloud.loadingState || !pointCloud.loadingState.loaded) return;
  
  const { loadingState } = pointCloud;
  
  const {
    loadStartTime,
    totalPoints,
    loadedPoints,
    pointsToAdd,
    pointCloud: cloudObject,
    rays,
    rayTimes,
    rayGroup
  } = loadingState;
  
  const { LOADING } = LIGHT_PRISM.TERRAIN;
  
  const currentTime = Date.now() / 1000;
  
  // Wait for initial delay
  if (currentTime < loadStartTime) return;
  
  // Check if all points have been loaded
  if (loadedPoints >= totalPoints) return;
  
  // Calculate how many points to load in this frame
  const elapsedTime = currentTime - loadStartTime;
  const targetProgress = Math.min(elapsedTime / LOADING.TOTAL_DURATION, 1.0);
  const targetPoints = Math.floor(totalPoints * targetProgress);
  
  // Number of points to add in this frame
  const pointsToAddCount = Math.min(
    LOADING.POINTS_PER_FRAME, 
    targetPoints - loadedPoints
  );
  
  // Update existing rays - remove old ones and update positions
  if (LOADING.ENABLE_LOADING_RAYS) {
    const rayDuration = LOADING.RAY_DURATION;
    
    // Check for rays that need to be removed or updated
    for (let i = rays.length - 1; i >= 0; i--) {
      const ray = rays[i];
      const rayAge = currentTime - rayTimes[i];
      
      // Remove rays that are too old
      if (rayAge > rayDuration) {
        rayGroup.remove(ray);
        ray.geometry.dispose();
        rays.splice(i, 1);
        rayTimes.splice(i, 1);
      } 
      // Update ray positions - keep origin fixed, but update target position as needed
      else if (ray.userData) {
        // Get the current position attribute
        const positionAttribute = ray.geometry.getAttribute('position');
        const positions = positionAttribute.array;
        
        // Get a reference to the point cloud group
        const pointCloudGroup = loadingState.pointCloudGroup;
        
        // Create a vector for the local target position
        const localTarget = new THREE.Vector3(
          ray.userData.localTargetPos.x,
          ray.userData.localTargetPos.y,
          ray.userData.localTargetPos.z
        );
        
        // Apply the current point cloud transformation to get the updated world position
        // This accounts for any rotation or movement of the point cloud
        localTarget.applyMatrix4(pointCloudGroup.matrix);
        
        // Keep the first 3 values (prism position) unchanged
        // Update target position (second 3 values) with transformed coordinates
        positions[3] = localTarget.x;
        positions[4] = localTarget.y; 
        positions[5] = localTarget.z;
        
        // Update the stored world position
        ray.userData.worldTargetPos = {
          x: localTarget.x,
          y: localTarget.y,
          z: localTarget.z
        };
        
        // Mark the attribute as needing update
        positionAttribute.needsUpdate = true;
      }
    }
  }
  
  if (pointsToAddCount > 0 && cloudObject) {
    // Get the position and color buffers
    const positions = cloudObject.geometry.attributes.position.array;
    const colors = cloudObject.geometry.attributes.color.array;
    
    // Add points
    for (let i = 0; i < pointsToAddCount; i++) {
      // Get next point to add
      if (loadingState.loadedPoints < pointsToAdd.length) {
        const pointData = pointsToAdd[loadingState.loadedPoints];
        const idx = pointData.index * 3;
        
        // Update the position
        positions[idx] = pointData.position.x;
        positions[idx + 1] = pointData.position.y;
        positions[idx + 2] = pointData.position.z;
        
        // Update the color
        colors[idx] = pointData.color.r;
        colors[idx + 1] = pointData.color.g;
        colors[idx + 2] = pointData.color.b;
        
        // Create ray from the second prism to the point
        // Only create a ray for every Nth point based on the RAY_FREQUENCY setting
        if (LOADING.ENABLE_LOADING_RAYS && 
            loadingState.loadedPoints < totalPoints && 
            loadingState.loadedPoints % LOADING.RAY_FREQUENCY === 0) {
          // Use our helper function to create the ray
          loadingState.createRay(pointData.position);
        }
        
        loadingState.loadedPoints++;
      } else {
        break;
      }
    }
    
    // Mark attributes as needing update
    cloudObject.geometry.attributes.position.needsUpdate = true;
    cloudObject.geometry.attributes.color.needsUpdate = true;
    
    // If we just finished loading
    if (loadingState.loadedPoints >= totalPoints) {
      console.log('Point cloud loading complete');
      
      // Clear all rays when loading is complete
      if (LOADING.ENABLE_LOADING_RAYS) {
        // Remove all rays
        for (const ray of loadingState.rays) {
          if (ray.geometry) ray.geometry.dispose();
          loadingState.rayGroup.remove(ray);
        }
        loadingState.rays = [];
        loadingState.rayTimes = [];
      }
    }
  }
};

/**
 * Clean up point cloud resources
 */
const cleanupLidarPointCloud = (scene, pointCloud) => {
  if (!pointCloud) return;
  
  const { pointCloudGroup, loadingState } = pointCloud;
  
  // Remove from scene
  scene.remove(pointCloudGroup);
  
  // Clean up the point cloud
  if (loadingState.pointCloud) {
    loadingState.pointCloud.geometry.dispose();
    loadingState.pointCloud.material.dispose();
  }
  
  // Clean up rays
  if (loadingState.rays) {
    for (const ray of loadingState.rays) {
      if (ray.geometry) ray.geometry.dispose();
    }
    loadingState.rays = [];
    loadingState.rayTimes = [];
  }
  
  if (loadingState.rayMaterial) {
    loadingState.rayMaterial.dispose();
  }
  
  if (loadingState.rayGroup) {
    // Remove the ray group from the scene first
    scene.remove(loadingState.rayGroup);
    
    // Clear all children from ray group
    while (loadingState.rayGroup.children.length > 0) {
      const ray = loadingState.rayGroup.children[0];
      loadingState.rayGroup.remove(ray);
      if (ray.geometry) ray.geometry.dispose();
      if (ray.material) ray.material.dispose();
    }
  }
  
  // Clean up any other THREE objects
  pointCloudGroup.traverse((obj) => {
    if (obj.geometry) {
      obj.geometry.dispose();
    }
    if (obj.material) {
      if (Array.isArray(obj.material)) {
        obj.material.forEach(mat => mat.dispose());
      } else {
        obj.material.dispose();
      }
    }
  });
  
  // Clear cached buffer if it exists
  if (typeof LASLoader !== 'undefined') {
    const loader = new LASLoader();
    loader.cachedBuffer = null;
  }
};

export { createLidarPointCloud, updateLidarPointCloud, cleanupLidarPointCloud };